import { StandardError } from '@/modules/errors'
import { refuseTooLargeCloudFile } from '@/modules/upload/components/FileDropzone/checkIncomingFiles'
import { PDF_MIME_TYPE } from '@/modules/upload/constants'
import type { AddFiles, FileDropzoneItem } from '@/modules/upload/types'
import { captureException } from '@/services/monitoring/captureException'
import { inBatches } from './batches'
import { type CloudDownloadFailure, failureOf } from './CloudDownloadError'
import { CLOUD_FILE_SIZE_LIMIT_BYTES } from './sizeLimit'
import type { CloudSource, PickedCloudFile } from './types'
import { downloadVerifiedPdf } from './verifiedPdf'

interface PickedFileFailure {
  name: string
  failure: CloudDownloadFailure
}

type PickedFileOutcome = { item: FileDropzoneItem } | PickedFileFailure

const isFailure = (outcome: PickedFileOutcome) => 'failure' in outcome

// Some pickers have no size limit, so a file listed over the limit is refused without a download.
const fetchVerifiedFile = async (
  picked: PickedCloudFile,
): Promise<PickedFileOutcome> => {
  if (picked.size > CLOUD_FILE_SIZE_LIMIT_BYTES) {
    const refused = refuseTooLargeCloudFile(
      picked.name,
      CLOUD_FILE_SIZE_LIMIT_BYTES,
    )
    return { item: refused }
  }
  try {
    const blob = await downloadVerifiedPdf(picked)
    return { item: new File([blob], picked.name, { type: PDF_MIME_TYPE }) }
  } catch (error) {
    return { name: picked.name, failure: failureOf(error) }
  }
}

const fetchInBatches = async (pickedFiles: PickedCloudFile[]) => {
  const outcomes: PickedFileOutcome[] = []
  for (const batch of inBatches(pickedFiles)) {
    outcomes.push(...(await Promise.all(batch.map(fetchVerifiedFile))))
  }
  return outcomes
}

const reportFailure = (source: CloudSource, failure: CloudDownloadFailure) =>
  captureException(
    new StandardError(`Could not get a file from ${source.label}`, 0, {
      provider: source.label,
      failure,
    }),
  )

const describeCloudFailure = (
  source: CloudSource,
  name: string,
  failure: CloudDownloadFailure,
) => {
  if (failure === 'too-many-at-once') {
    return `Could not get ${name} from ${source.label}: too many files at once. Pick fewer files and try again.`
  }
  const message = `Could not get ${name} from ${source.label}. Try again.`
  const advice = failure === 'forbidden' ? source.blockedLinkAdvice : undefined
  return advice ? `${message} ${advice}` : message
}

// Downloads every picked file, adds the good ones through the drop zone's checks, and returns
// one message per file that could not be brought in. One failure never stops the others.
export const importFromCloud = async (
  source: CloudSource,
  pickedFiles: PickedCloudFile[],
  addFiles: AddFiles,
) => {
  const outcomes = await fetchInBatches(pickedFiles)
  const items = outcomes.flatMap((outcome) =>
    'item' in outcome ? [outcome.item] : [],
  )
  if (items.length > 0) await addFiles(items)
  const failures = outcomes.filter(isFailure)
  for (const { failure } of failures) reportFailure(source, failure)
  return failures.map(({ name, failure }) =>
    describeCloudFailure(source, name, failure),
  )
}
